import React, { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button, message } from 'antd';
import { useSelector } from 'react-redux';
import { CloseOutlined, InfoCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import { isAppModule, PRODUCT_MODE_MAP } from '@src/utils/constants';
import {
  aiAlertsCustomMetricsLimit,
  isAdminRoleSelector,
  ecoAIAlert2AccessSelector,
  aiAlertFusionVsiSelector,
} from '@src/store/common/selectors';
import SensitivityByMetricForm from './SensitivityForms/SensitivityConfigForm';
import useMetricsMeta from '@src/hooks/useMetricsMeta';
import Header from './Header';
import {
  selectedProductAtom,
  fullSensitivityConfigAtom,
  selectedKpiIdAtom,
  drawerMetricsAtom,
  aiCohortDrawerAtom,
} from './atoms';
import MetricsDrawer from './MetricDrawer';
import ConcurrentPlaysSection from './ConcurrentPlaysSection';
import {
  ContentHeaderWrapper,
  EditMetricWrapper,
  FooterWrapper,
  StyledDrawer,
  StyledSearch,
  SensitivityNotice,
} from './styled';
import { validateSensitivityConfigs } from './errorUtils';
import { useUpdateSensitivity } from '@src/components/ai-alerts/hooks/useSensitivity';
import useAIAlertMetricsMeta from '../../hooks/useAIAlertMetricsMeta';
import { Retry } from '@src/components/common';
import { findDifferencesWithResult } from './utils';
import { productMap } from '@src/utils/aiAlerts/mapping';
import { useGetAIAlertMetricsCategories } from '../../hooks/useAIAlertMetricsCategory';
import AICohortDrawer from '../AICohort';
import { useGlobalFeature } from '@globalStore/globalFeatureFlagsStore';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';

const SensitivityDrawer = ({ isPopupOpen, cancelEdit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const updateSensitivity = useUpdateSensitivity();
  const { forceUpdateUserDefinedMetricsMeta } = useMetricsMeta();
  const fullSensitivityData = useAtomValue(fullSensitivityConfigAtom);
  const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);
  const {
    aiAlertMetricsMeta,
    sensitivityResponseData: sensitivityData,
    kpiId,
    isFetching,
    isError,
    refetch,
  } = useAIAlertMetricsMeta(isPopupOpen, fullSensitivityData);
  const selectedKpiId = useAtomValue(selectedKpiIdAtom);
  const setSelectedKpiId = useSetAtom(selectedKpiIdAtom);
  const searchRef: any = useRef(null);
  const isAiAlertsCustomMetricsLimit = useSelector(aiAlertsCustomMetricsLimit);
  const aiAlertsMetricsCustomMetricsLimit = useSelector(aiAlertsCustomMetricsLimit);
  const ecoAIAlert2 = useSelector(ecoAIAlert2AccessSelector);
  const enableDPICustomDimension = useGlobalFeature(globalFeatureNames.DPI_CUSTOM_DIMENSION);
  const [openMetricDrawer, setOpenMetricDrawer] = useState(false);
  const [disable, setDisable] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [openAICohortDrawer, setOpenAICohortDrawer] = useAtom(aiCohortDrawerAtom);
  const [drawerMetrics, setDrawerMetrics] = useAtom(drawerMetricsAtom);
  const isAdminUser = useSelector(isAdminRoleSelector);
  const fusionVsiEnabled = useSelector(aiAlertFusionVsiSelector);
  const isPcpFromNewSystem = useGlobalFeature(globalFeatureNames.IS_PCP_FROM_NEW_SYSTEM);
  const { categoryMap } = useGetAIAlertMetricsCategories({
    aiAlertMetricsMeta,
    searchText,
    product: selectedProduct,
  });

  const selectedMetrics: any =
    aiAlertMetricsMeta.filter((metric) => metric.isCustom).map((metric) => metric.name) || [];

  const handleSearch = (event) => {
    event?.stopPropagation();
    setSearchText(event.target.value);
  };

  const onOpenMetricDrawer = () => {
    setOpenMetricDrawer(true);
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus({
          cursor: 'start',
        });
      }
    }, 250);
    // refresh template metrics
    if (isAppModule) {
      forceUpdateUserDefinedMetricsMeta(); // refreshing once as store is defined on load
    }
  };

  const closeConfigDrawer = () => {
    setDrawerMetrics([]);
    cancelEdit();
  };

  const closeMetricDrawer = () => {
    setDrawerMetrics(selectedMetrics);
    setOpenMetricDrawer(false);
  };

  const onBack = () => {
    setDrawerMetrics(selectedMetrics);
    setOpenMetricDrawer(false);
  };

  const saveSensitivity = async () => {
    setIsLoading(true);
    const result = findDifferencesWithResult(fullSensitivityData, sensitivityData);

    await updateSensitivity.mutate(
      { sensitivity: result, kpiId: selectedKpiId },
      {
        onSuccess: () => {
          setIsLoading(false);
          message.success('Sensitivity updated successfully');
          cancelEdit();
        },
        onError: () => {
          message.error('There was an error while saving the sensitivity. Please try again later.');
          setIsLoading(false);
        },
      },
    );
  };

  useEffect(() => {
    if (!drawerMetrics?.length && selectedMetrics.length) {
      setDrawerMetrics(selectedMetrics);
    }
  }, [drawerMetrics, selectedMetrics]);

  // Initialize the KPI dropdown from the persisted synopsis kpiId (defaults to Conviva Good).
  useEffect(() => {
    if (typeof kpiId === 'number') {
      setSelectedKpiId(kpiId);
    }
  }, [kpiId, setSelectedKpiId]);

  useEffect(() => {
    setDisable(validateSensitivityConfigs(fullSensitivityData, ecoAIAlert2));
  }, [selectedProduct, fullSensitivityData]);

  return isPopupOpen ? (
    <>
      <StyledDrawer
        title={
          <Header
            selectedProduct={selectedProduct}
            onProductChange={(value) => setSelectedProduct(value)}
          />
        }
        width={window.innerWidth > 1200 ? 1200 : window.innerWidth - 100}
        closable={false}
        keyboard={true}
        onClose={closeConfigDrawer}
        extra={<CloseOutlined onClick={closeConfigDrawer} />}
        open={isPopupOpen}
        styles={{
          header: { paddingBottom: 0, border: 'none' },
          body: { padding: '0 35px 22px 22px' },
        }}
        forceRender={true}
      >
        <Retry auto={false} isLoading={isFetching} showError={isError} fetchData={refetch} progress>
          <SensitivityNotice>
            <InfoCircleTwoTone twoToneColor="#ffa500" style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Removing a metric or changing SPI settings does not auto-resolve existing alerts — any
              alerts already active for that metric/SPI will continue in their active state.
            </span>
          </SensitivityNotice>
          {isAppModule ? (
            <ContentHeaderWrapper>
              {isAiAlertsCustomMetricsLimit > 0 && isAdminUser && (
                <EditMetricWrapper>
                  <Button
                    type="primary"
                    style={{ marginRight: '10px' }}
                    onClick={onOpenMetricDrawer}
                    disabled={aiAlertsMetricsCustomMetricsLimit == 0}
                    ghost
                  >
                    <PlusOutlined />
                    Metrics
                  </Button>
                  <InfoCircleTwoTone />
                  {aiAlertsMetricsCustomMetricsLimit == 0 ? (
                    <span>{`You don't have provision to add custom metrics.`}</span>
                  ) : (
                    <span>
                      {` Configured AI Alerts for Flow and Custom Metrics: `}
                      <b>{drawerMetrics.length}</b> {` (Limit: `}
                      <b>{aiAlertsMetricsCustomMetricsLimit}</b>)
                    </span>
                  )}
                </EditMetricWrapper>
              )}
              <StyledSearch
                placeholder="Search metric"
                allowClear
                value={searchText}
                onChange={handleSearch}
              />
            </ContentHeaderWrapper>
          ) : null}
          <SensitivityByMetricForm
            categoryMap={categoryMap}
            sensitivityList={fullSensitivityData.filter(
              (s) => s.app === productMap[selectedProduct],
            )}
            savedSensitivityList={sensitivityData}
            searchText={searchText}
          />
          {/* Main drawer Cancel/Save sit above the Concurrent Plays card so each
              section owns its own save action (PCP is persisted to MySQL via a
              separate endpoint and must not share this Save button). */}
          <FooterWrapper>
            <Button onClick={closeConfigDrawer}>{isAdminUser ? 'Cancel' : 'Back'}</Button>
            <Button
              type="primary"
              style={{ marginLeft: '10px' }}
              onClick={saveSensitivity}
              loading={isLoading}
              disabled={
                // disable ||
                !isAdminUser ||
                isError ||
                isLoading ||
                (!fusionVsiEnabled && selectedProduct === PRODUCT_MODE_MAP.AI)
              }
            >
              Save
            </Button>
          </FooterWrapper>
          {!isPcpFromNewSystem && <ConcurrentPlaysSection isPopupOpen={isPopupOpen} />}
        </Retry>
      </StyledDrawer>

      {openMetricDrawer && (
        <MetricsDrawer
          isDrawerOpen={openMetricDrawer}
          setOpenMetricDrawer={setOpenMetricDrawer}
          onClose={closeMetricDrawer}
          onBack={onBack}
          searchRef={searchRef}
          selectedProduct={selectedProduct}
        />
      )}

      {/* AI Cohort Drawer */}
      {enableDPICustomDimension ? (
        <AICohortDrawer
          visible={openAICohortDrawer.open}
          onClose={() =>
            setOpenAICohortDrawer({ open: false, metrics: [], key: '', isFlow: false })
          }
          sensitivityConfigDrawerOpen={isPopupOpen}
        />
      ) : null}
    </>
  ) : null;
};

export default React.memo(SensitivityDrawer);
